import React, { useMemo, useState } from 'react';
import { Repeat, Clock, Link as LinkIcon, AlertTriangle, Info } from 'lucide-react';
import {
  LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend
} from 'recharts';

// Demo: Retention & Repeat Purchase Drivers
// - Time-to-repeat distribution (days)
// - Cohort retention curve (Month 1..6)
// - SKU ladders (gateway → attachment), simple co‑purchase matrix collapsed to top pairs
// - Churn risk: simple score from recency & frequency

function seedRand(seed){ let t=seed|0; return ()=>{ t^=t<<13; t^=t>>17; t^=t<<5; return Math.abs(t)/0x7fffffff; }; }

function genTTRepeat(){
  const rnd = seedRand(777);
  const buckets = [7,14,21,30,45,60,90];
  return buckets.map((d,i)=>({ days: d, customers: Math.round(500 + rnd()*400 - i*50) }));
}

function genCohort(){
  const rnd = seedRand(888);
  const months = [1,2,3,4,5,6];
  return months.map(m=>({ month:`M${m}`, retention: Math.max(5, Math.round(60*Math.exp(-0.35*(m-1)) + (rnd()-0.5)*6)) }));
}

function genPairs(){
  const pairs = [
    { pair: 'Running Shoes → Socks', lift: 2.8 },
    { pair: 'Phone → Case', lift: 2.1 },
    { pair: 'Laptop → Mouse', lift: 1.9 },
    { pair: 'Shampoo → Conditioner', lift: 1.7 },
    { pair: 'Camera → SD Card', lift: 1.6 },
  ];
  return pairs;
}

function genChurn(){
  // risk score 0-100
  const arr = [];
  for(let i=0;i<6;i++){
    const segment = ['VIP','Loyal','Promising','New','At‑Risk','Dormant'][i];
    const score = [12,18,35,22,68,85][i];
    arr.push({ segment, score });
  }
  return arr;
}

export default function RetentionDriversDemo(){
  const ttr = useMemo(()=>genTTRepeat(),[]);
  const cohort = useMemo(()=>genCohort(),[]);
  const pairs = useMemo(()=>genPairs(),[]);
  const churn = useMemo(()=>genChurn(),[]);

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm">
        <div className="flex items-center gap-3">
          <Repeat className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Retention & Repeat Purchase Drivers (Demo)</h1>
            <p className="text-sm text-gray-500 mt-1">Time‑to‑repeat, cohort retention, SKU ladders, churn risk</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Time-to-Repeat */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-gray-500" />
            <h3 className="font-medium text-gray-800 text-sm">Time‑to‑Repeat (days)</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ttr}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="days" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip />
                <Bar dataKey="customers" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-gray-600 mt-2">Peak between 14–30 days → schedule reminders/coupons accordingly.</div>
        </div>

        {/* Cohort Retention */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="w-4 h-4 text-gray-500" />
            <h3 className="font-medium text-gray-800 text-sm">Cohort Retention (M1–M6)</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart data={cohort}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#9ca3af" />
                <YAxis unit="%" stroke="#9ca3af" />
                <Tooltip formatter={(v)=>[`${v}%`,'Retention']} />
                <Line type="monotone" dataKey="retention" stroke="#10b981" strokeWidth={2} dot />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-gray-600 mt-2">Target M2 drop with “second‑order” play; test CRM cadence.</div>
        </div>

        {/* Churn Risk by Segment */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-gray-500" />
            <h3 className="font-medium text-gray-800 text-sm">Churn Risk (by segment)</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={churn}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="segment" stroke="#9ca3af" />
                <YAxis domain={[0,100]} stroke="#9ca3af" />
                <Tooltip />
                <Bar dataKey="score" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-gray-600 mt-2">Act on “At‑Risk” and “Dormant” with winback bundles & reminders.</div>
        </div>
      </div>

      {/* SKU Ladders */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 text-sm">Top SKU Ladders (Gateway → Attachment)</h3>
          <div className="text-xs text-gray-500 flex items-center"><Info className="w-3.5 h-3.5 mr-1" />Lift vs random attach rate</div>
        </div>
        <div className="grid grid-cols-5 gap-3">
          {pairs.map(p=>(
            <div key={p.pair} className="border border-gray-200 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-800">{p.pair}</div>
              <div className="text-xs text-green-700 mt-1">Lift x{p.lift.toFixed(1)}</div>
              <div className="text-xs text-gray-600 mt-1">Play: Add “Complete the set” bundle & PDP reco</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


